<template>
  <div class="static-quiz-play">
    <div v-if="phase === 'quiz'" class="panel quiz-panel">
      <div class="progress">
        第 {{ currentIndex + 1 }} / {{ questions.length }} 题 ·
        {{ currentQuestion && currentQuestion["题型"] }}
      </div>

      <div class="question-body" v-html="questionHtml"></div>

      <template v-if="currentQuestion && isMultiple">
        <van-checkbox-group v-model="multiAnswer">
          <div v-for="opt in currentOptions" :key="opt.key" class="option-row">
            <van-checkbox :name="opt.key" shape="square">
              <span class="option-label">{{ opt.key }}. {{ opt.text }}</span>
            </van-checkbox>
          </div>
        </van-checkbox-group>
      </template>

      <template v-else-if="currentQuestion">
        <van-radio-group v-model="singleAnswer">
          <div
            v-for="opt in currentOptions"
            :key="opt.key"
            class="option-row"
            @click="singleAnswer = opt.key"
          >
            <van-radio :name="opt.key">
              <span class="option-label">{{ opt.key }}. {{ opt.text }}</span>
            </van-radio>
          </div>
        </van-radio-group>
      </template>

      <div class="footer-btns">
        <van-button
          type="info"
          block
          round
          :disabled="!canGoNext"
          @click="goNext"
        >
          {{ isLastQuestion ? "完成并查看结果" : "下一题" }}
        </van-button>
      </div>
    </div>

    <div v-else class="panel result-panel">
      <div class="result-summary">
        共 {{ questions.length }} 题，答对 {{ correctCount }} 题
      </div>
      <van-button block round type="primary" class="mb" @click="backToList">
        选择题库
      </van-button>
      <van-button block round plain hairline type="info" @click="retrySameBank">
        重做本套
      </van-button>

      <div v-for="(item, idx) in resultItems" :key="idx" class="review-card">
        <div class="review-index">{{ idx + 1 }}. {{ item.type }}</div>
        <div class="review-q" v-html="item.questionHtml"></div>
        <div
          v-for="opt in item.options"
          :key="opt.key"
          class="review-option"
          :class="reviewOptionClass(item, opt.key)"
        >
          <span class="review-key">{{ opt.key }}.</span>
          <span>{{ opt.text }}</span>
        </div>
        <div v-if="item.parse" class="review-parse">{{ item.parse }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { Toast } from "vant";
import {
  findBankById,
  extractOptions,
  normalizeAnswerLetters,
  answersMatch,
} from "./staticQuizUtils";

export default {
  name: "StaticQuizPlay",
  data() {
    return {
      phase: "quiz",
      bankTitle: "",
      questions: [],
      currentIndex: 0,
      singleAnswer: "",
      multiAnswer: [],
      userAnswers: [],
    };
  },
  computed: {
    currentQuestion() {
      return this.questions[this.currentIndex] || null;
    },
    questionHtml() {
      const q = this.currentQuestion;
      return q ? String(q["题目"] || "") : "";
    },
    currentOptions() {
      return this.currentQuestion ? extractOptions(this.currentQuestion) : [];
    },
    isMultiple() {
      const t = this.currentQuestion && this.currentQuestion["题型"];
      return t === "多选题";
    },
    isLastQuestion() {
      return this.currentIndex >= this.questions.length - 1;
    },
    canGoNext() {
      if (!this.currentQuestion) return false;
      if (this.isMultiple) return this.multiAnswer.length > 0;
      return !!this.singleAnswer;
    },
    correctCount() {
      let n = 0;
      this.questions.forEach((q, i) => {
        if (answersMatch(this.userAnswers[i], q["正确答案"])) n += 1;
      });
      return n;
    },
    resultItems() {
      return this.questions.map((raw, idx) => ({
        type: raw["题型"] || "",
        questionHtml: String(raw["题目"] || ""),
        options: extractOptions(raw),
        userAnswer: this.userAnswers[idx],
        correct: raw["正确答案"],
        parse: raw["解析"] ? String(raw["解析"]) : "",
      }));
    },
  },
  watch: {
    currentIndex() {
      this.resetAnswerInputs();
    },
    bankTitle(t) {
      document.title = t || "刷题";
    },
  },
  mounted() {
    const err = this.applyBankFromRoute(this.$route);
    if (err === "missing") {
      Toast.fail("请从选择题库进入");
      this.$router.replace({ name: "StaticQuiz" });
    } else if (err === "empty") {
      this.$router.replace({ name: "StaticQuiz" });
    }
  },
  beforeRouteUpdate(to, from, next) {
    const err = this.applyBankFromRoute(to);
    if (err === "missing") {
      Toast.fail("请从选择题库进入");
      next({ name: "StaticQuiz", replace: true });
      return;
    }
    if (err === "empty") {
      next({ name: "StaticQuiz", replace: true });
      return;
    }
    next();
  },
  methods: {
    applyBankFromRoute(route) {
      const raw = route.query.bank;
      const id = raw == null ? "" : decodeURIComponent(String(raw));
      const b = findBankById(id);
      if (!b) return "missing";
      const list = b.load();
      if (!Array.isArray(list) || !list.length) {
        Toast("题库为空或格式错误");
        return "empty";
      }
      this.bankTitle = b.title;
      this.questions = list;
      this.currentIndex = 0;
      this.userAnswers = [];
      this.resetAnswerInputs();
      this.phase = "quiz";
      return null;
    },
    resetAnswerInputs() {
      this.singleAnswer = "";
      this.multiAnswer = [];
    },
    goNext() {
      if (!this.canGoNext) {
        Toast("请选择答案");
        return;
      }
      const ua = this.isMultiple
        ? normalizeAnswerLetters(this.multiAnswer.join(""))
        : normalizeAnswerLetters(this.singleAnswer);
      this.$set(this.userAnswers, this.currentIndex, ua);

      if (this.isLastQuestion) {
        this.phase = "result";
        document.title = "答题结果";
        return;
      }
      this.currentIndex += 1;
    },
    reviewOptionClass(item, key) {
      const correct = normalizeAnswerLetters(item.correct).includes(key);
      const user = normalizeAnswerLetters(item.userAnswer).includes(key);
      const cls = [];
      if (correct) cls.push("is-correct");
      if (user && !correct) cls.push("is-wrong");
      return cls.join(" ");
    },
    backToList() {
      this.$router.push({ name: "StaticQuiz" });
    },
    retrySameBank() {
      const title = this.bankTitle;
      const copy = this.questions;
      this.currentIndex = 0;
      this.userAnswers = [];
      this.resetAnswerInputs();
      this.bankTitle = title;
      this.questions = copy;
      this.phase = "quiz";
      document.title = title || "刷题";
    },
  },
};
</script>

<style scoped lang="scss">
.static-quiz-play {
  min-height: 100vh;
  background: #f5f6f7;
  padding-bottom: 24px;
}

.panel {
  padding: 12px 0;
}

.quiz-panel {
  padding-left: 16px;
  padding-right: 16px;
}

.progress {
  font-size: 13px;
  color: #969799;
  margin-bottom: 12px;
}

.question-body {
  font-size: 16px;
  line-height: 1.6;
  color: #323233;
  margin-bottom: 16px;
}

.option-row {
  background: #fff;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
}

.option-label {
  font-size: 15px;
  line-height: 1.5;
  color: #323233;
}

.footer-btns {
  margin-top: 24px;
  padding-bottom: 8px;
}

.result-panel {
  padding: 12px 16px 32px;
}

.result-summary {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 16px;
  color: #323233;
}

.mb {
  margin-bottom: 10px;
}

.review-card {
  background: #fff;
  border-radius: 10px;
  padding: 14px;
  margin-top: 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.review-index {
  font-size: 13px;
  color: #969799;
  margin-bottom: 8px;
}

.review-q {
  font-size: 15px;
  line-height: 1.55;
  color: #323233;
  margin-bottom: 12px;
}

.review-option {
  padding: 8px 10px;
  border-radius: 6px;
  margin-bottom: 6px;
  font-size: 14px;
  line-height: 1.45;
  background: #fafafa;
  border: 1px solid transparent;

  &.is-correct {
    background: #e8f8ef;
    border-color: #07c160;
    color: #1a7f48;
  }

  &.is-wrong {
    background: #fef0f0;
    border-color: #ee0a24;
    color: #c3101f;
  }
}

.review-key {
  font-weight: 600;
  margin-right: 4px;
}

.review-parse {
  margin-top: 10px;
  font-size: 13px;
  color: #646566;
  line-height: 1.5;
  border-top: 1px solid #ebedf0;
  padding-top: 10px;
}

::v-deep .van-radio__label,
::v-deep .van-checkbox__label {
  width: 100%;
}
</style>
